import * as React from 'react'
import { FlatList, View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import IncidentCard from '@components/incidentCard/IncidentCard'

import useReducer from '@store/useReducer'
import * as IncidentStore from '@store/incident/incident'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import { EtatWorkflowIncidentEnum } from '@api/enum'

import api from '@api/api'
import logger from '@utils/logger'

interface Props {
  // search params
  filter?: EtatWorkflowIncidentEnum
  title?: string
}

const IncidentListScreen = ({ filter, title }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const decodedTitle = decodeURIComponent(title || '')

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const filtered = React.useMemo(() => incidents.filter((i) => filter === i.status), [incidents])

  React.useEffect(() => {
    Promise.all([
      api.incident.search({ siteId: site.id, etatsIncident: filter ? [filter] : [] }),
      api.familles.all(site.id, true),
      api.localisation.all(site.id, true),
      api.societe.all(site.id),
    ])
      .then(([incidents, activities, places, providers]) => {
        IncidentStore.actions.setIncidents(incidents)
        FamilleStore.actions.setActivities(activities)
        LocationStore.actions.setPlaces(places)
        SocieteStore.actions.setServices(providers)
      })
      .catch(logger.error)
  }, [])

  const renderIncidentCard = ({ item }: { item: IncidentResponse }) => (
    <IncidentContainer
      activeOpacity={0.9}
      onPress={() => navigation.push(`/incidentDetail/${item.id}`, { incidentId: item.id })}>
      <IncidentCard incident={item} />
    </IncidentContainer>
  )

  return (
    <Root header={isPhone ? decodedTitle : undefined} onLeft={navigation.goBack}>
      <View>
        {!isPhone && <Header title={decodedTitle || i18n.t('screens.site.incidents')} />}

        <FlatList
          data={filtered}
          renderItem={renderIncidentCard}
          contentContainerStyle={{
            flexWrap: 'wrap',
            flexDirection: isPhone ? 'column' : 'row',
            padding: isPhone ? 16 : 24,
            justifyContent: isPhone ? 'center' : 'flex-start',
          }}
          ListEmptyComponent={<MissingInfo>{i18n.t('screens.incidentList.empty')}</MissingInfo>}
        />
      </View>
    </Root>
  )
}

export default IncidentListScreen

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const IncidentContainer = styled(TouchableOpacity)`
  margin-bottom: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 16)}px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `padding: 4px 0px;` : `margin-right: 16px;`)};
`
